/**
 * amenity-note — note normalization + codepoint-counting helpers
 * (SCRUM-141 / DS-010-5).
 *
 * These two pure functions MIRROR the backend `AmenityNote` value object
 * (SCRUM-138): trim leading/trailing whitespace, and treat a whitespace-only
 * (or empty / nullish) note as `null`. Applying the same rule on the FE before
 * the optimistic write AND before the PUT payload means the optimistic cache
 * equals the server-reconciled value — no flicker on `onSettled`
 * (plan §"Note normalization mirrors BE" / codex MEDIUM-6).
 *
 * The 500-codepoint CAP is deliberately NOT re-implemented here — that is the
 * shared `setPropertyAmenitiesItemSchema` from `@artemis/validators` (CLAUDE.md
 * FE §4: validators are the single source of truth). `noteCodepointLength`
 * exists only to drive the editor's `N/500` live counter; it counts Unicode
 * codepoints (`[...v].length`), NOT UTF-16 code units, so a surrogate-pair
 * emoji counts as 1 and the counter matches the schema's cap semantics.
 */

/**
 * Normalize a raw note to the BE `AmenityNote` VO shape:
 *   - `null` / `undefined` → `null`
 *   - empty or whitespace-only (after trim) → `null`
 *   - otherwise the leading/trailing-trimmed string
 *
 * Interior content is preserved verbatim (no sanitization) — an
 * injection-shaped value (`<script>…`, `' OR 1=1`) passes through opaque; the
 * cap is the schema's responsibility, so a > 500-codepoint value is NOT
 * truncated here.
 */
export function normalizeNote(raw: string | null | undefined): string | null {
  if (raw == null) {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * Count the Unicode codepoints in a string (`[...value].length`). Used by the
 * editor's live `N/500` counter so a multi-code-unit emoji counts as its true
 * codepoint length, matching the shared schema's `[...v].length` cap.
 */
export function noteCodepointLength(value: string): number {
  return [...value].length;
}
